// src/hooks/useFirebaseDedup.ts
import { useRef, useCallback } from 'react';
import { FirestoreCache, CacheItem, CacheOptions } from '../services/firestoreCache';

/**
 * Hook to deduplicate Firebase requests by caching results
 */
export function useFirebaseDedup() {
  // Use a ref for the pending requests to persist between renders
  const pendingRequestsRef = useRef<Record<string, Promise<any> | undefined>>({});
  const firestoreCache = FirestoreCache.getInstance();

  /**
   * Deduplicate Firebase requests by caching results for a specified time
   * @param cacheKey Unique key to identify this request in the cache
   * @param requestFn Function that returns a Promise for the Firebase request
   * @param cacheDuration How long to cache the result in milliseconds (default: 10000)
   * @param storageType Storage type to use (default: 'memory')
   * @returns The result of the Firebase request
   */
  const dedupRequest = useCallback(async <T>(
    cacheKey: string,
    requestFn: () => Promise<T>,
    cacheDuration: number = 10000,
    storageType: CacheOptions['storageType'] = 'memory'
  ): Promise<T> => {
    // First, check if there's a cached version
    const cachedData = await firestoreCache.get<T>(cacheKey, {
      cacheDuration,
      storageType
    });
    
    if (cachedData !== null) {
      return cachedData;
    }
    
    // Check if we have a pending request for this key
    if (pendingRequestsRef.current[cacheKey] !== undefined) {
      return pendingRequestsRef.current[cacheKey] as Promise<T>;
    }
    
    // Create a new request and store it in pending requests
    try {
      pendingRequestsRef.current[cacheKey] = requestFn();
      const result = await pendingRequestsRef.current[cacheKey];
      
      // Cache the result
      await firestoreCache.set(cacheKey, result, {
        cacheDuration,
        storageType
      });
      
      // Clear the pending request
      delete pendingRequestsRef.current[cacheKey];
      
      return result;
    } catch (error) {
      // Clear the pending request on error
      delete pendingRequestsRef.current[cacheKey];
      throw error;
    }
  }, []);
  
  /**
   * Clear a specific item from the cache
   */
  const clearCacheItem = useCallback((cacheKey: string) => {
    firestoreCache.remove(cacheKey);
  }, []);
  
  /**
   * Clear the entire cache
   */
  const clearCache = useCallback(() => {
    firestoreCache.clear();
  }, []);

  return { dedupRequest, clearCacheItem, clearCache };
}